import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import adminService from "../../api/services/adminService";
import Loading from "../../components/Loading";
import SectionCard from "../../components/common/SectionCard";
import Modal from "../../components/common/Modal";
import AdminPanelUserModal from "../../Modals/AdminPanelUserModal";
import PaginationBar from "../../components/common/PaginationBar";
import toast from "react-hot-toast";

const AdminPanelUsers = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [backendPage, setBackendPage] = useState(1);
  const [total, setTotal] = useState(0);
  const backendLimit = 50;
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [deleteUser, setDeleteUser] = useState(null);
  const [trigger, setTrigger] = useState(false);

  const [frontendPage, setFrontendPage] = useState(1);
  const frontendLimit = 15;

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setIsLoading(true);
        const result = await adminService.getAllAdminPanelUsers(backendPage);
        const allUsers = result?.users || [];
        setUsers(allUsers);
        setTotal(result?.total || allUsers.length);
        setFrontendPage(1);
      } catch (error) {
        if (error?.status === 401) {
          toast.error("Session expired. Please log in again.");
          navigate("/login");
          return;
        }
        toast.error(error?.message || "Failed to fetch admin panel users");
      } finally {
        setIsLoading(false);
      }
    };

    fetchUsers();
  }, [backendPage, trigger, navigate]);

  const openModal = () => {
    setEditingUser(null);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingUser(null);
    setTrigger(!trigger);
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setIsModalOpen(true);
  };

  const handleDelete = (user) => {
    setDeleteUser(user);
  };

  const confirmDelete = async () => {
    if (!deleteUser) return;
    try {
      await adminService.deleteAdminPanelUser(deleteUser.id);
      toast.success("User deleted successfully!");
      setDeleteUser(null);
      setTrigger(!trigger);
    } catch (error) {
      if (error?.status === 401) {
        toast.error("Session expired. Please log in again.");
        navigate("/login");
        return;
      }
      toast.error(error?.message || "Failed to delete user");
    }
  };

  const paginatedUsers = users.slice(
    (frontendPage - 1) * frontendLimit,
    frontendPage * frontendLimit
  );

  const filteredUsers = paginatedUsers.filter((user) => {
    const fullName = `${user.firstName || ""} ${user.lastName || ""}`.toLowerCase();
    return (
      fullName.includes(searchQuery.toLowerCase()) ||
      (user.email && user.email.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (user.contactNo && user.contactNo.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (user.roleName && user.roleName.toLowerCase().includes(searchQuery.toLowerCase()))
    );
  });

  const totalPages = Math.ceil(users.length / frontendLimit);

  const handleFrontendPageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) setFrontendPage(newPage);
  };

  const getRoleColor = (roleName) => {
    switch (roleName) {
      case "Admin":
        return "bg-purple-100 text-purple-800";
      case "Author":
        return "bg-blue-100 text-blue-800";
      case "CSR":
        return "bg-green-100 text-green-800";
      case "Developer":
        return "bg-amber-100 text-amber-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <div className="w-full p-4 overflow-x-hidden">
      <SectionCard
        title="Admin Panel Users"
        subtitle="Manage admin, author, CSR, and developer users"
        action={
          <button
            type="button"
            onClick={openModal}
            className="cursor-pointer rounded-lg text-xs md:text-sm border border-red-500 bg-red-500 px-4 py-2 text-white font-semibold hover:bg-red-600 transition-colors shadow-md">
            Add User
          </button>
        }>
        <div className="mb-4">
          <input
            type="text"
            placeholder="Search by name, email, phone, or role..."
            className="w-full max-w-md rounded-lg border border-gray-300 px-3 py-2 text-xs md:text-sm text-gray-900 placeholder-gray-400 focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <div className="overflow-x-auto">
          {isLoading ? (
            <div className="text-center py-6">
              <div className="inline-block animate-spin rounded-full h-10 w-10 border-4 border-red-500 border-t-transparent" />
              <p className="mt-4 text-gray-600 font-medium text-sm">
                Loading admin panel users...
              </p>
            </div>
          ) : (<>
          <table className="min-w-full border-separate text-xs md:text-sm border-spacing-0">
            <thead>
              <tr>
                <th className="border-b border-gray-200 px-2 py-2 text-left font-semibold text-gray-500">
                  SR. NO
                </th>
                <th className="border-b border-gray-200 px-2 py-2 text-left font-semibold text-gray-500">
                  First Name
                </th>
                <th className="border-b border-gray-200 px-2 py-2 text-left font-semibold text-gray-500">
                  Last Name
                </th>
                <th className="border-b border-gray-200 px-2 py-2 text-left font-semibold text-gray-500">
                  Email
                </th>
                <th className="border-b border-gray-200 px-2 py-2 text-left font-semibold text-gray-500">
                  Contact
                </th>
                <th className="border-b border-gray-200 px-2 py-2 text-left font-semibold text-gray-500">
                  Role
                </th>
                <th className="border-b border-gray-200 px-2 py-2 text-left font-semibold text-gray-500">
                  Created At
                </th>
                <th className="border-b border-gray-200 px-2 py-2 text-right font-semibold text-gray-500">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {!isLoading && filteredUsers.length === 0 ? (
                <tr>
                  <td
                    colSpan={8}
                    className="p-4 text-center text-xs md:text-sm text-gray-500">
                    No admin panel users found
                  </td>
                </tr>
              ) : (
                filteredUsers.map((user, idx) => (
                  <tr key={user.id} className="hover:bg-gray-50/50">
                    <td className="border-b border-gray-100 px-2 py-2 text-xs md:text-sm">
                      {(frontendPage - 1) * frontendLimit + idx + 1}
                    </td>
                    <td className="border-b border-gray-100 px-2 py-2 text-xs md:text-sm font-medium text-gray-900">
                      {user.firstName ?? "—"}
                    </td>
                    <td className="border-b border-gray-100 px-2 py-2 text-xs md:text-sm">
                      {user.lastName ?? "—"}
                    </td>
                    <td className="border-b border-gray-100 px-2 py-2 text-xs md:text-sm">
                      {user.email ?? "—"}
                    </td>
                    <td className="border-b border-gray-100 px-2 py-2 text-xs md:text-sm">
                      {user.contactNo ?? "—"}
                    </td>
                    <td className="border-b border-gray-100 px-2 py-2 text-xs md:text-sm">
                      <span
                        className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${getRoleColor(
                          user.roleName
                        )}`}>
                        {user.roleName ?? "N/A"}
                      </span>
                    </td>
                    <td className="border-b border-gray-100 px-2 py-2 text-xs md:text-sm">
                      {user.createdDate
                        ? new Date(user.createdDate).toLocaleDateString()
                        : "—"}
                    </td>
                    <td className="border-b border-gray-100 px-2 py-2 text-right text-xs md:text-sm">
                      <div className="inline-flex gap-2">
                        <button
                          type="button"
                          onClick={() => handleEdit(user)}
                          className="cursor-pointer rounded-md border border-gray-300 bg-white px-2 py-1 text-xs md:text-sm hover:bg-gray-50">
                          Edit
                        </button>
                        {user.roleName !== "Admin" && (
                          <button
                            type="button"
                            onClick={() => handleDelete(user)}
                            className="cursor-pointer rounded-md border border-red-500 bg-red-50 px-2 py-1 text-xs md:text-sm text-red-700 hover:bg-red-100">
                            Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          </>)}
        </div>

        {totalPages > 0 && !isLoading && (
          <PaginationBar
            page={frontendPage}
            totalPages={totalPages}
            totalItems={users.length}
            pageSize={frontendLimit}
            label="users"
            onPageChange={handleFrontendPageChange}
          />
        )}
      </SectionCard>

      <AdminPanelUserModal
        isOpen={isModalOpen}
        onClose={closeModal}
        editingUser={editingUser}
        trigger={trigger}
        setTrigger={setTrigger}
      />

      <Modal
        open={!!deleteUser}
        title="Confirm Delete"
        onClose={() => setDeleteUser(null)}
        footer={
          <>
            <button
              type="button"
              onClick={() => setDeleteUser(null)}
              className="cursor-pointer rounded-lg text-xs md:text-sm border border-gray-300 bg-white px-3 py-2 text-gray-900">
              Cancel
            </button>
            <button
              type="button"
              onClick={confirmDelete}
              className="cursor-pointer rounded-lg text-xs md:text-sm border border-red-500 bg-red-500 px-4 py-2 text-white font-semibold hover:bg-red-600 transition-colors shadow-md">
              Delete
            </button>
          </>
        }>
        <div className="text-gray-700">
          Are you sure you want to delete{" "}
          <strong>
            {deleteUser?.firstName} {deleteUser?.lastName}
          </strong>
          ? This action cannot be undone.
        </div>
      </Modal>
    </div>
  );
};

export default AdminPanelUsers;
